/**
 * Integrytas Group - Application State & Session Fingerprinting (ASF)
 * Page: ASF.DIAGNOSTICS
 *
 * Read-heavy diagnostic interface. Search captured environment snapshots, and
 * reconstruct the exact framework environment at the millisecond of an audit
 * event (separating environmental factors from the core data mutation).
 */

import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import type { AuthSession } from '../../types/auth';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';

interface SnapRow {
  asf_snapshot_id: number; user_id: number | null; captured_at: string; action: string | null;
  entity_name: string | null; record_id: string | null; une_workspace_id: number | null;
  screen_code: string | null; tab_id: string | null; role_ids: unknown; role_matrix: unknown;
  fingerprint_hash: string | null; ip_address: string | null; session_token: string | null; session_started_at: string | null;
}

const SNAP_GRID_COLS: GridColumnDef[] = [
  { key: 'captured_at',     header: 'When',        defaultWidth: 180 },
  { key: 'action',          header: 'Action',      defaultWidth: 100 },
  { key: 'entity_name',     header: 'Target',      defaultWidth: 180 },
  { key: 'screen_code',     header: 'Screen',      defaultWidth: 160 },
  { key: 'fingerprint_hash',header: 'Fingerprint', defaultWidth: 160 },
  { key: 'ip_address',      header: 'IP',          defaultWidth: 120 },
];

export interface AsfDiagnosticsPageProps { session: AuthSession }

export function AsfDiagnosticsPage({ session }: AsfDiagnosticsPageProps) {
  return (
    <PersonalizationProvider pageCode="ASF.DIAGNOSTICS" session={session}>
      <AsfDiagnosticsPageInner session={session} />
    </PersonalizationProvider>
  );
}

function AsfDiagnosticsPageInner({ session }: AsfDiagnosticsPageProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<SnapRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fEntity, setFEntity] = useState('');
  const [fRecord, setFRecord] = useState('');
  const [open, setOpen] = useState<number | null>(null);
  const [auditId, setAuditId] = useState('');
  const [recon, setRecon] = useState<{ mutation: unknown; environment: unknown } | null>(null);
  const detail = useExpandedRows<number>();

  // TODO: wire relations — asf_snapshot is a flat diagnostic/log row with no
  // child collections to surface; the disclosure renders inert (spacer).
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      let q = apiDb.from('asf_snapshot')
        .select('asf_snapshot_id, user_id, captured_at, action, entity_name, record_id, une_workspace_id, screen_code, tab_id, role_ids, role_matrix, fingerprint_hash, ip_address, session_token, session_started_at')
        .order('captured_at', { ascending: false }).limit(100);
      if (fEntity.trim()) q = q.eq('entity_name', fEntity.trim());
      if (fRecord.trim()) q = q.eq('record_id', fRecord.trim());
      const { data, error: e } = await q;
      if (e) throw e;
      setRows((data ?? []) as SnapRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }, [fEntity, fRecord]);
  useEffect(() => { void reload(); }, [reload]);

  const reconstruct = async () => {
    if (!auditId.trim()) return;
    setBusy(true); setError(null); setRecon(null);
    try {
      const { data, error: e } = await apiDb.rpc('asf_reconstruct', { p_audit_log_id: Number(auditId.trim()) });
      if (e) throw e;
      const r = data as { ok: boolean; mutation?: unknown; environment?: unknown; error_code?: string };
      if (!r?.ok) throw new Error(r?.error_code ?? 'ASF.RECONSTRUCT_FAILED');
      setRecon({ mutation: r.mutation, environment: r.environment });
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('asf.title', 'Session Diagnostics')}</span>
        <input value={fEntity} onChange={e => setFEntity(e.target.value)} placeholder={t('asf.placeholder.entity', 'entity')} className="h-7 w-40 rounded border border-csm-border px-2 font-mono text-csm-xs" />
        <input value={fRecord} onChange={e => setFRecord(e.target.value)} placeholder={t('asf.placeholder.recordId', 'record id')} className="h-7 w-28 rounded border border-csm-border px-2 font-mono text-csm-xs" />
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('asf.search', 'Search')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('asf.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      <div className="flex-1 overflow-auto p-3">
        {error && (
          <ErrorBanner
            message={t('asf.load_failed', "Couldn't load session diagnostics.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <div className="mb-3 flex flex-wrap items-end gap-2 rounded border border-csm-border bg-white p-2">
          <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('asf.reconstructByAuditId', 'Reconstruct by Audit ID')}</span>
          <input value={auditId} onChange={e => setAuditId(e.target.value)} placeholder={t('asf.placeholder.auditLogId', 'audit_log_id')} className="h-8 w-40 rounded border border-csm-border px-2 font-mono text-csm-sm" />
          <button onClick={() => void reconstruct()} className="h-8 rounded border border-sidebar bg-sidebar px-3 text-csm-sm text-white hover:bg-sidebar-alt">{t('asf.reconstruct', 'Reconstruct')}</button>
        </div>
        {recon && (
          <div className="mb-4 grid grid-cols-2 gap-3">
            <div className="rounded border border-csm-border bg-white p-2">
              <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('asf.coreDataMutation', 'Core Data Mutation')}</div>
              <pre className="max-h-72 overflow-auto font-mono text-csm-2xs text-csm-ink-soft">{JSON.stringify(recon.mutation, null, 2)}</pre>
            </div>
            <div className="rounded border border-primary/40 bg-primary/5 p-2">
              <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-primary">{t('asf.frameworkEnvironment', 'Framework Environment')}</div>
              <pre className="max-h-72 overflow-auto font-mono text-csm-2xs text-csm-ink-soft">{recon.environment ? JSON.stringify(recon.environment, null, 2) : t('asf.noSnapshotLinked', 'No snapshot linked to this audit event.')}</pre>
            </div>
          </div>
        )}

        <PersonalizedGrid componentKey="grid" columns={SNAP_GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border bg-csm-surface-alt px-1 py-1" title={t('asf.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t(`asf.col.${col.key}`, col.header)}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-right" style={{ width: '1%' }}>{t('asf.col.env', 'Env')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <Fragment key={r.asf_snapshot_id}>
                    <tr className="hover:bg-csm-surface-alt">
                      <td className="border-b border-csm-border-soft px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                        <RelatedExpandButton
                          open={detail.isOpen(r.asf_snapshot_id)}
                          hasChildren={hasRelated(r.asf_snapshot_id)}
                          onToggle={() => detail.toggle(r.asf_snapshot_id)}
                          expandLabel={t('asf.related.expand', 'Show related records')}
                          collapseLabel={t('asf.related.collapse', 'Hide related')}
                          tooltip={t('asf.related.tooltip', 'Show related records')}
                        />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className="border-b border-csm-border-soft px-2 py-1"
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'captured_at'      && <span className="text-csm-2xs text-csm-watermark">{new Date(r.captured_at).toLocaleString()}.{String(new Date(r.captured_at).getMilliseconds()).padStart(3, '0')}</span>}
                          {col.key === 'action'           && <span className="text-csm-xs">{r.action}</span>}
                          {col.key === 'entity_name'      && <span className="font-mono text-csm-2xs">{r.entity_name}{r.record_id ? `#${r.record_id}` : ''}</span>}
                          {col.key === 'screen_code'      && <span className="font-mono text-csm-2xs">{r.screen_code ?? '—'}{r.tab_id ? ` / ${r.tab_id}` : ''}</span>/* fk-display-ok: raw app-state fingerprint screen tag */}
                          {col.key === 'fingerprint_hash' && <span className="font-mono text-csm-2xs">{r.fingerprint_hash ?? '—'}</span>}
                          {col.key === 'ip_address'       && <span className="font-mono text-csm-2xs">{r.ip_address ?? '—'}</span>}
                        </td>
                      ))}
                      <td className="border-b border-csm-border-soft px-2 py-1 text-right"><button onClick={() => setOpen(open === r.asf_snapshot_id ? null : r.asf_snapshot_id)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-2xs hover:bg-csm-surface-alt">{open === r.asf_snapshot_id ? t('asf.hide', 'Hide') : t('asf.view', 'View')}</button></td>
                    </tr>
                    {detail.isOpen(r.asf_snapshot_id) && (
                      <tr>
                        <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                          <RelatedChildrenPanel groups={buildRelatedGroups(r.asf_snapshot_id)} session={session} />
                        </td>
                      </tr>
                    )}
                    {open === r.asf_snapshot_id && (
                      <tr>
                        <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt px-2 py-1">
                          <div className="grid grid-cols-2 gap-3">
                            <div><div className="text-csm-2xs font-semibold uppercase text-csm-ink-soft">{t('asf.rolesAtCapture', 'Roles (at capture)')}</div><pre className="max-h-48 overflow-auto font-mono text-csm-2xs text-csm-ink-soft">{JSON.stringify(r.role_ids, null, 2)}</pre></div>
                            <div><div className="text-csm-2xs font-semibold uppercase text-csm-ink-soft">{t('asf.permissionMatrix', 'Permission Matrix')}</div><pre className="max-h-48 overflow-auto font-mono text-csm-2xs text-csm-ink-soft">{JSON.stringify(r.role_matrix, null, 2)}</pre></div>
                          </div>
                          <div className="mt-1 text-csm-2xs text-csm-watermark">session {r.session_token ?? '—'} · started {r.session_started_at ? new Date(r.session_started_at).toLocaleString() : '—'} · workspace #{r.une_workspace_id ?? '—'}</div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {rows.length === 0 && !busy && <tr><td colSpan={visibleCols.length + 2} className="px-2 py-6"><EmptyState variant={fEntity.trim() || fRecord.trim() ? 'no-match' : 'no-data'} title={t('asf.empty.snapshots', 'No snapshots.')} /></td></tr>}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
      </div>
    </div>
  );
}
